import { HollowedButton, NormalButton } from '@shared/designSystem/Button';
import { HorizontalContainer, VerticalContainer } from '@shared/designSystem/Containers';
import { TextInput, TextInputRef } from '@shared/designSystem/TextInput';
import { ErrorText, Text } from '@shared/designSystem/Texts';
import { Modal } from '@shared/designSystem/messages/Modal';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { useEffect, useRef } from 'react';
import { useResetPasswordModalViewModel } from './ResetPasswordModalViewModel';

interface ResetPasswordModalScreenProps {
    isOpen: boolean;
    initialEmail: string;
    onClose: () => void;
    onResetPassword: (email: string) => Promise<void>;
}

const ResetPasswordModalScreen: React.FC<ResetPasswordModalScreenProps> = ({
    isOpen,
    initialEmail,
    onClose,
    onResetPassword
}) => {
    const viewModel = useResetPasswordModalViewModel({ isOpen, initialEmail, onResetPassword });
    const emailInputRef = useRef<TextInputRef>(null);

    useEffect(() => {
        if (!isOpen || viewModel.wasSent) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            emailInputRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [isOpen, viewModel.wasSent]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={"Recuperar contraseña".localized()}
            width="460px"
            maxWidth="460px"
        >
            {viewModel.wasSent ? (
                <VerticalContainer gap={designSystemStyles.gapMd}>
                    <Text text={"Si existe una cuenta con ese email, te enviamos un mail con los pasos para crear una nueva contraseña. Revisá tu casilla y la carpeta de spam.".localized()} />
                    <HorizontalContainer justifyContent="flex-end">
                        <NormalButton text={"Entendido".localized()} onClick={onClose} />
                    </HorizontalContainer>
                </VerticalContainer>
            ) : (
                <VerticalContainer gap={designSystemStyles.gapMd}>
                    <Text text={"Ingresá el email de tu cuenta y te enviamos un mail para crear una nueva contraseña.".localized()} />
                    <TextInput
                        ref={emailInputRef}
                        label={"Email".localized()}
                        placeholder={"Ingresar email".localized()}
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        value={viewModel.email}
                        onChange={viewModel.updateEmail}
                        onEnterPress={viewModel.sendResetEmail}
                        disabled={viewModel.isSending}
                    />
                    {viewModel.errorMessage && <ErrorText text={viewModel.errorMessage} />}
                    <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapSm}>
                        <HollowedButton
                            text={"Cancelar".localized()}
                            onClick={onClose}
                            disabled={viewModel.isSending}
                        />
                        <NormalButton
                            text={"Enviar mail".localized()}
                            onClick={viewModel.sendResetEmail}
                            loading={viewModel.isSending}
                        />
                    </HorizontalContainer>
                </VerticalContainer>
            )}
        </Modal>
    );
};

export default ResetPasswordModalScreen;
